import type { JSX } from 'react';

import type { BuilderStore } from '../state.js';

import { ToggleField } from './Field.js';

export interface StatusBarEditorProps {
  store: BuilderStore;
}

/**
 * Top status bar configuration — visibility + per-icon toggles. The
 * `showCurrentUser` row is gated by Enterprise (`ssoDisplay` flag) at
 * runtime; the schema accepts it at every tier so Builder lets Free
 * users preview "what would this look like with SSO."
 */
export function StatusBarEditor({ store }: StatusBarEditorProps): JSX.Element {
  const profile = store.activeProfile;
  const { statusBar } = profile;
  const ssoHonored = store.config.enabledFeatures.includes('ssoDisplay');

  return (
    <div className="editor">
      <header className="editor__head">
        <h2 className="editor__title">Status bar</h2>
        <p className="editor__sub">
          Top app bar. Visibility + per-icon toggles. SSO display is
          Enterprise-only (<code>ssoDisplay</code> flag).{' '}
          <a href="https://mobilauncher.com/docs/customize#status-bar" target="_blank" rel="noopener noreferrer" className="editor__doc-link">
            Read the docs →
          </a>
        </p>
      </header>

      <fieldset className="editor__group">
        <legend className="editor__group-label">Visibility</legend>
        <ToggleField
          label="Visible"
          hint="When off, the bar is hidden entirely."
          value={statusBar.visible}
          onChange={(value) => store.patchStatusBar({ visible: value })}
        />
      </fieldset>

      <fieldset className="editor__group">
        <legend className="editor__group-label">Icons</legend>
        <ToggleField
          label="Show clock"
          value={statusBar.showClock ?? false}
          onChange={(value) => store.patchStatusBar({ showClock: value })}
        />
        <ToggleField
          label={`Show current user${ssoHonored ? '' : ' (Enterprise)'}`}
          hint={
            ssoHonored
              ? "Renders the signed-in user's display name from the device's MDM agent."
              : 'Locked until ssoDisplay is in enabledFeatures.'
          }
          value={statusBar.showCurrentUser ?? false}
          onChange={(value) => store.patchStatusBar({ showCurrentUser: value })}
        />
      </fieldset>
      {/*
        Battery + signal toggles intentionally removed — the renderer has
        no way to read real device state from a WebView (Web Battery API
        is deprecated for privacy; there's no signal-strength API at all).
        They'll come back when an MDM-agent integration ships (SOTI JS
        Agent first, others per customer ask) and we can bind these icons
        to real telemetry. Existing configs that set showBattery /
        showSignal remain valid; the renderer just ignores them.
      */}
    </div>
  );
}
